import { createJSONStorage, type PersistStorage, type StateStorage } from 'zustand/middleware';

/**
 * ⭐⭐ ONE SAVED COPY PER ACCOUNT — for the persisted stores that hold an account's own data.
 *
 * ⚠️⚠️ `persist` kept ONE localStorage key per store, whoever was signed in, and a sign-out
 * does not reload the page. So the next account on this device read the last one's copy:
 * onboarding "finished" for a brand-new account, the assessment history, the social
 * notifications and pacts, the Free-mode list, the kanban flow. A reset on sign-out
 * (lib/signOutReset) is not enough for these stores, for two reasons:
 *   - some of what they keep exists ONLY here — a running Free-mode timer, a custom kanban
 *     flow, onboarding answers before Enter, the assessment history Home reads without
 *     loading it. A clear destroys it for the account that comes back;
 *   - a store whose chunk had not loaded at the sign-out is never reset, and hydrates the
 *     old copy when it does load.
 *
 * So each account gets its own key — `<name>::<user id>` — and on every change of account
 * (a sign-out, a sign-in, a switch) every store here stops writing, goes back to its
 * initial state, then reads the new account's own copy. The old account's copy stays on
 * the device for when it signs in again, and nobody else is shown it.
 *
 * ⚠️ A load or a write still on its way for the account that left is the store's to drop:
 * note `accountGeneration()` before the first await, compare after each one.
 *
 * ⚠️ The account comes FROM authStore (`reportAccount`) — this module imports nothing of
 * the app, so a test that mocks authStore still loads every store, and no cycle can form.
 */

/** What becomes of the copy saved under the store's old key, the one shared by everyone. */
export type LegacyCopy =
  /** Deleted unread — for a store that reloads all of it from the cloud anyway. */
  | 'drop'
  /**
   * Taken by the account signed in when the page loaded: the auth key names whoever last
   * used this device, so the copy is almost certainly theirs. Dropped by any account that
   * signs in after that — the old copy is never handed to a later account.
   */
  | 'claim'
  /** The copy names its owner: that account takes it, anyone else leaves it for them. */
  | ((state: unknown) => string | null | undefined);

export const accountKey = (name: string, userId: string): string => `${name}::${userId}`;

/** The legacy copy of a synced store names its owner: the account it was loaded for. */
export const cachedUserIdOwner = (saved: unknown): string | null | undefined =>
  (saved as { cachedUserId?: string | null } | undefined)?.cachedUserId;

/** `undefined` until authStore first reports; `null` = nobody signed in, or a change mid-way. */
let account: string | null | undefined;
let generation = 0;
/** An account has changed since the page loaded — a `'claim'` is no longer the boot account's. */
let changed = false;
/** The scoped stores are being emptied or handed their account's saved copy (see `swapping`). */
let swapping = false;

interface Scoped {
  reset: () => void;
  rehydrate: () => void;
}
const scoped: Scoped[] = [];

function device(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function takeLegacy(ls: Storage, name: string, who: string, legacy: LegacyCopy): string | null {
  const old = ls.getItem(name);
  if (old === null) return null;
  if (typeof legacy === 'function') {
    let owner: string | null | undefined;
    try {
      owner = legacy((JSON.parse(old) as { state?: unknown }).state);
    } catch {
      owner = null;
    }
    if (owner && owner !== who) return null;
    if (!owner) {
      ls.removeItem(name);
      return null;
    }
  } else if (legacy === 'drop' || changed) {
    ls.removeItem(name);
    return null;
  }
  // ⚠️ No copy under the account's key here: `persist` writes what it hydrated straight back.
  ls.removeItem(name);
  return old;
}

/** `persist`'s `storage` for a store whose saved copy belongs to one account. */
export function accountStorage<S>(legacy: LegacyCopy = 'drop'): PersistStorage<S> | undefined {
  const perAccount: StateStorage = {
    getItem: (name) => {
      const ls = device();
      if (!ls || !account) return null;
      try {
        return ls.getItem(accountKey(name, account)) ?? takeLegacy(ls, name, account, legacy);
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      const ls = device();
      if (!ls || !account) return;
      try {
        ls.setItem(accountKey(name, account), value);
      } catch {
        /* full or blocked — the copy is a convenience, the cloud or the next write has it */
      }
    },
    removeItem: (name) => {
      const ls = device();
      if (!ls || !account) return;
      try {
        ls.removeItem(accountKey(name, account));
      } catch {
        /* blocked */
      }
    },
  };
  return createJSONStorage<S>(() => perAccount);
}

/**
 * Put a store (persisted with `accountStorage`) under the account lifecycle: back to its
 * initial state and its new account's copy whenever the account changes.
 */
export function scopeToAccount<T>(store: {
  setState: (state: T, replace: true) => void;
  getInitialState: () => T;
  persist: { rehydrate: () => Promise<void> | void };
}): void {
  scoped.push({
    reset: () => store.setState(store.getInitialState(), true),
    rehydrate: () => void store.persist.rehydrate(),
  });
}

/** Moves on every change of account. A load or write that noted another number belongs to the account that left. */
export function accountGeneration(): number {
  return generation;
}

/**
 * ⚠️ True while the scoped stores are emptied or handed their account's saved copy — both
 * synchronous, inside `reportAccount`. Neither is an edit: a store that writes on every
 * change (a sync subscription) skips them, or each sign-in and each page load would send
 * one more full read of every synced row.
 */
export function swappingAccount(): boolean {
  return swapping;
}

function swap(stores: () => void): void {
  swapping = true;
  try {
    stores();
  } finally {
    swapping = false;
  }
}

/** authStore: the signed-in account when it loads, and after every change of its user. */
export function reportAccount(id: string | null): void {
  if (account === undefined) {
    account = id;
    // A store that loaded before authStore read nothing — read its account's copy now.
    if (id) swap(() => { for (const s of scoped) s.rehydrate(); });
    return;
  }
  if (id === account) return;
  account = null; // the resets below write nowhere — least of all into the next account's copy
  generation += 1;
  changed = true;
  swap(() => {
    for (const s of scoped) s.reset();
    account = id;
    if (id) for (const s of scoped) s.rehydrate();
  });
}
